import React from 'react';
import isEmpty from 'lodash/isEmpty';
import some from 'lodash/some';
import every from 'lodash/every';
import SelectOption from '../SelectOption/SelectOption';
import Typography from '../Typography/Typography';
import CreateNew from './CreateNew';
import { styled } from '@mui/material/styles';

function getQASelectAllState(isIndeterminateSelection, isAllOptionsSelected) {
  if (isIndeterminateSelection) {
    return 'indeterminate';
  }

  if (isAllOptionsSelected) {
    return 'checked';
  }

  return 'unchecked';
}

const MenuRoot = styled('div')({
  width: '100%'
});

const SelectAll = styled('div')(({ theme }) => ({
  '&:focus-visible': {
    backgroundColor: theme.palette.action.focus,
    outline: 'none'
  },
  '&:focus': {
    backgroundColor: theme.palette.action.focus,
    outline: 'none'
  }
}));

const CounterText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  paddingTop: theme.spacing(1),
  paddingRight: theme.spacing(1)
}));

const Menu = props => {
  const { children, MenuProps, options, ...RestMenuProps } = props;
  const {
    countSelectionText,
    selectedOptions = [],
    maxAmountOfVisibleOptionsToShowSelectAll,
    creatable,
    onCreate,
    onCreateNew,
    focusOnSelect,
    onSelectAllClick,
    isMultiSelectionMode,
    onKeyDown,
    AddButtonProps
  } = MenuProps;

  const showSelectAll =
    !isEmpty(options) &&
    maxAmountOfVisibleOptionsToShowSelectAll >= options.length;

  const isSelectedOption = value =>
    selectedOptions.some(option => option.value === value.value);

  const isAllChecked = every(options, isSelectedOption);

  const isSomeOptionsSelected = some(options, isSelectedOption);

  const isIndeterminate = !isAllChecked && isSomeOptionsSelected;

  const toggleSelectAll = () => {
    onSelectAllClick();
  };

  const qaSelectAllState = getQASelectAllState(isIndeterminate, isAllChecked);
  const shouldDisplayCreateNew = AddButtonProps || (creatable && onCreate);

  return (
    <MenuRoot data-qa-id="multi-select-menu_options" {...RestMenuProps}>
      {!isEmpty(options) && (
        <>
          {countSelectionText && (
            <CounterText
              align="right"
              variant="caption"
              display="block"
              data-qa-id="multi-select-menu_selected-options">
              {countSelectionText}
            </CounterText>
          )}
          {shouldDisplayCreateNew && (
            <CreateNew
              onChange={onCreateNew}
              onClose={focusOnSelect}
              isMultiSelectionMode={isMultiSelectionMode}
              AddButtonProps={AddButtonProps}
            />
          )}
          {showSelectAll && (
            <SelectAll
              role="button"
              tabIndex={0}
              onClick={event => {
                event.stopPropagation();
                if (event.keyCode === 32) {
                  // spacebar, this means the user wants to toggle the actual focused option
                  return;
                }
                toggleSelectAll();
              }}
              onKeyDown={e => {
                if (e.key === ' ') {
                  e.stopPropagation();
                  toggleSelectAll();
                } else if (onKeyDown) {
                  onKeyDown(e);
                }
              }}
              data-qa-id="select-all"
              data-qa-state={qaSelectAllState}>
              <SelectOption
                label="Select All"
                value="Select All"
                isSelected={isAllChecked}
                isIndeterminate={isIndeterminate}
                isCheckbox={true}
              />
            </SelectAll>
          )}
        </>
      )}
      {children}
    </MenuRoot>
  );
};

export default Menu;
